import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Flag } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/chat-admin`;

/**
 * Members ▸ Chat — the club app's chat (docs/OL_ACCESS_LINKS.md §10; chat is OL ↔ mobile app only).
 * Club-wide switches (chat on/off, direct + group messages on/off) and reported messages (remove / restore).
 */
export default function ChatAdminPanel({ orgId }) {
  const [s, setS] = useState(null);
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([axios.get(`${API}/${orgId}/settings`, { withCredentials: true }),
        axios.get(`${API}/${orgId}/reported`, { withCredentials: true })]);
      setS(a.data.settings); setRows(b.data.messages || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load chat settings'); setRows([]); }
  }, [orgId]);
  useEffect(() => { if (orgId) load(); }, [orgId, load]);
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next);
    try { await axios.put(`${API}/${orgId}/settings`, next, { withCredentials: true }); toast.success('Saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); load(); }
  };
  const hide = async (m, hidden) => {
    try { await axios.post(`${API}/${orgId}/messages/${m.id}/hide`, { hidden }, { withCredentials: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not change'); }
  };
  if (!s || !rows) return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  return (
    <div className="space-y-4" data-testid="chat-admin">
      <p className="text-xs text-slate-500">Chat happens in the club app only (phones). Team chats are created for each team this season; families also get a family chat. Direct and group messages are adults-only.</p>
      <div className="rounded border p-3 space-y-2 dark:border-slate-700">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.enabled} onChange={e => save({ enabled: e.target.checked })} data-testid="chat-enabled" /> Chat is on for the club app</label>
        <label className={`flex items-center gap-2 text-sm ${s.enabled ? '' : 'opacity-50'}`}><input type="checkbox" disabled={!s.enabled} checked={s.direct_messages} onChange={e => save({ direct_messages: e.target.checked })} data-testid="chat-dm" /> Allow direct and group messages (adults only)</label>
      </div>
      <div>
        <div className="text-sm font-semibold flex items-center gap-1 mb-1"><Flag className="h-4 w-4" />Reported messages</div>
        {rows.length === 0 ? <p className="text-xs text-slate-500">Nothing reported.</p> : (
          <div className="rounded border divide-y dark:border-slate-700 dark:divide-slate-700">
            {rows.map(m => (
              <div key={m.id} className="p-2 text-sm flex gap-3 items-start" data-testid={`reported-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-500">{m.chat} · {m.sender} · {new Date(m.created_at).toLocaleString()}</div>
                  <div className={m.hidden || m.deleted ? 'line-through text-slate-400' : ''}>{m.text || (m.deleted ? '(deleted by sender)' : '')}</div>
                  <div className="text-xs text-amber-600">{m.reports.map(r => r.reason || 'reported').join(' · ')}</div>
                </div>
                {!m.deleted && <button className={`text-xs underline ${m.hidden ? 'text-emerald-600' : 'text-red-600'}`} onClick={() => hide(m, !m.hidden)}>{m.hidden ? 'Restore' : 'Remove'}</button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
